import { BaseApiService } from './BaseApiService';
import { logger } from '../utils/logger';
import { env } from '../config/environment';
import type { 
  CompanyMetrics, 
  AnalystRecommendations, 
  SECFiling, 
  FinancialStatements, 
  FinancialStatement,
  DetailedFinancialData,
  BalanceSheetData,
  IncomeStatementData,
  CashFlowStatementData,
  EarningsData,
  IncomeStatementPeriod,
  CashFlowPeriod,
  EarningsPeriod
} from '../types/market';

interface FinnhubCompanyProfile {
  country: string;
  currency: string;
  exchange: string;
  ipo: string;
  marketCapitalization: number;
  name: string;
  phone: string;
  shareOutstanding: number;
  ticker: string;
  weburl: string;
  logo: string;
  finnhubIndustry: string;
}

interface FinnhubMetrics {
  metric: {
    '10DayAverageTradingVolume': number;
    '52WeekHigh': number;
    '52WeekLow': number;
    '52WeekLowDate': string;
    '52WeekHighDate': string;
    'beta': number;
    'peBasicExclExtraTTM': number;
    'peTTM': number;
    'epsBasicExclExtraTTM': number;
    'epsTTM': number;
    'dividendYieldIndicatedAnnual': number;
    'totalDebt/totalEquityQuarterly': number;
    'revenuePerShareTTM': number;
  };
}

interface FinnhubRecommendations {
  buy: number;
  hold: number;
  period: string;
  sell: number;
  strongBuy: number;
  strongSell: number;
  symbol: string;
}

interface FinnhubFilings {
  symbol: string;
  year: number;
  form: string;
  filedDate: string;
  acceptedDate: string;
  reportUrl: string;
  filingUrl: string;
}



export class FinancialDataService extends BaseApiService {
  constructor() {
    if (!env.hasApiKey('tradier')) {
      throw new Error('VITE_TRADIER_API_KEY environment variable is required');
    }

    // Use Tradier for financial data, fallback to Finnhub for other data
    super('https://api.tradier.com/beta', {
      'Authorization': `Bearer ${env.apis.tradier}`,
      'Accept': 'application/json',
    });
    
    this.finnhubApiKey = env.hasApiKey('finnhub') ? env.apis.finnhub : undefined;
  }

  private finnhubApiKey: string | undefined;

  private getApiKeyParam(): string {
    return `token=${this.finnhubApiKey}`;
  }

  private async getFinnhubData<T>(endpoint: string): Promise<T> {
    if (!this.finnhubApiKey) {
      throw new Error('Finnhub API key not available');
    }

    const response = await fetch(`https://finnhub.io/api/v1${endpoint}`, {
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`Finnhub API error: ${response.status} ${response.statusText}`, { 
        endpoint, 
        status: response.status,
        error: errorText 
      });
      throw new Error(`Finnhub API error: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getCompanyMetrics(symbol: string): Promise<CompanyMetrics> {
    try {
      if (!this.finnhubApiKey) {
        logger.warn('Finnhub API key not available, returning mock company metrics');
        return this.getMockCompanyMetrics(symbol);
      }

      const [profile, metrics] = await Promise.all([
        this.getFinnhubData<FinnhubCompanyProfile>(`/stock/profile2?symbol=${symbol}&${this.getApiKeyParam()}`),
        this.getFinnhubData<FinnhubMetrics>(`/stock/metric?symbol=${symbol}&metric=all&${this.getApiKeyParam()}`)
      ]);

      return {
        symbol: symbol.toUpperCase(),
        name: profile.name || symbol,
        marketCap: profile.marketCapitalization || 0,
        peRatio: metrics.metric?.peTTM || metrics.metric?.peBasicExclExtraTTM,
        eps: metrics.metric?.epsTTM || metrics.metric?.epsBasicExclExtraTTM,
        revenue: metrics.metric?.revenuePerShareTTM,
        debtToEquity: metrics.metric?.['totalDebt/totalEquityQuarterly'],
        dividendYield: metrics.metric?.dividendYieldIndicatedAnnual,
        beta: metrics.metric?.beta,
      };
    } catch (error) {
      logger.error('Error fetching company metrics from Finnhub', error);
      // Return mock data as fallback
      return this.getMockCompanyMetrics(symbol);
    }
  }

  async getRecommendations(symbol: string): Promise<AnalystRecommendations> {
    try {
      if (!this.finnhubApiKey) {
        throw new Error('Finnhub API key not available for recommendations');
      }

      const response = await this.getFinnhubData<FinnhubRecommendations[]>(
        `/stock/recommendation?symbol=${symbol}&${this.getApiKeyParam()}`
      );

      // Get the most recent recommendation
      const latest = response[0] || {
        buy: 0,
        hold: 0,
        sell: 0,
        strongBuy: 0,
        strongSell: 0,
        symbol,
        period: ''
      };

      return {
        symbol: symbol.toUpperCase(),
        buy: latest.buy || 0,
        hold: latest.hold || 0,
        sell: latest.sell || 0,
        strongBuy: latest.strongBuy || 0,
        strongSell: latest.strongSell || 0,
      };
    } catch (error) {
      logger.error('Error fetching recommendations from Finnhub', error);
      // Return mock recommendations on error
      return this.getMockRecommendations(symbol);
    }
  }

  async getSECFilings(symbol: string, limit: number = 10): Promise<SECFiling[]> {
    try {
      if (!this.finnhubApiKey) {
        return []; // Return empty array if no Finnhub key
      }

      const fromDate = new Date();
      fromDate.setFullYear(fromDate.getFullYear() - 2); // Get filings from last 2 years
      
      const response = await this.getFinnhubData<FinnhubFilings[]>(
        `/stock/filings?symbol=${symbol}&from=${fromDate.toISOString().split('T')[0]}&${this.getApiKeyParam()}`
      );

      return response
        .slice(0, limit)
        .map(filing => ({
          symbol: symbol.toUpperCase(),
          form: filing.form,
          filedDate: filing.filedDate,
          url: filing.reportUrl || filing.filingUrl,
          description: this.getFilingDescription(filing.form),
        }));
    } catch (error) {
      logger.error('Error fetching SEC filings from Finnhub', error);
      return this.getMockSECFilings(symbol, limit);
    }
  }

  private getFilingDescription(form: string): string {
    const descriptions: Record<string, string> = {
      '10-K': 'Annual Report',
      '10-Q': 'Quarterly Report',
      '8-K': 'Current Report',
      '10-K/A': 'Annual Report Amendment',
      '10-Q/A': 'Quarterly Report Amendment',
      'DEF 14A': 'Proxy Statement',
      'S-1': 'Registration Statement',
      '424B4': 'Prospectus',
    };

    return descriptions[form] || form;
  }

  async getCompanyNews(symbol: string, days: number = 7): Promise<any[]> {
    try {
      if (!this.finnhubApiKey) {
        return []; // Return empty array if no Finnhub key
      }

      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - days);
      
      const toDate = new Date();
      
      const response = await this.getFinnhubData<any[]>(
        `/company-news?symbol=${symbol}&from=${fromDate.toISOString().split('T')[0]}&to=${toDate.toISOString().split('T')[0]}&${this.getApiKeyParam()}`
      );

      return response.slice(0, 10); // Limit to 10 articles
    } catch (error) {
      logger.error('Error fetching company news from Finnhub', error);
      return [];
    }
  }

  async getDetailedFinancialData(symbol: string): Promise<DetailedFinancialData> {
    try {
      logger.debug('Fetching detailed financial data from Tradier', { symbol });
      
      const response = await this.get<any>(`/markets/fundamentals/financials?symbols=${symbol}`);
      
      if (!response || !Array.isArray(response) || response.length === 0) {
        logger.warn('No response or empty array from Tradier API');
        throw new Error('Financial data not available');
      }

      const financialData = response.find((item: any) => 
        item.request === symbol && item.results && item.results.length > 0
      );

      if (!financialData) {
        logger.warn('No financial data found for symbol', { symbol });
        throw new Error('Financial data not available');
      }

      logger.debug('Financial data found, processing detailed format');

      const companyWithFinancials = financialData.results.find((result: any) => 
        result.type === 'Company' && result.tables?.financial_statements_restate
      );

      if (!companyWithFinancials?.tables?.financial_statements_restate) {
        throw new Error('Financial statements not available');
      }

      const statements = companyWithFinancials.tables.financial_statements_restate;
      
      return this.processDetailedFinancialData(symbol, statements);
    } catch (error) {
      logger.error('Error fetching detailed financial data from Tradier', error);
      throw new Error('Financial data not available');
    }
  }

  private processDetailedFinancialData(symbol: string, statements: any): DetailedFinancialData {
    const incomeStatements = statements.income_statement || [];
    const balanceSheets = statements.balance_sheet || [];
    const cashFlowStatements = statements.cash_flow_statement || [];

    // Process Balance Sheet (typically only 3M period available)
    const balanceSheet3M = this.findStatementData(balanceSheets, 'period_3m');
    const balanceSheet = this.processBalanceSheet(balanceSheet3M);

    // Process Income Statement for multiple periods
    const incomeStatement = this.processIncomeStatement(incomeStatements);

    // Process Cash Flow Statement for multiple periods
    const cashFlowStatement = this.processCashFlowStatement(cashFlowStatements);

    // Process Earnings Data
    const earningsData = this.processEarningsData(incomeStatements);

    return {
      symbol: symbol.toUpperCase(),
      balanceSheet,
      incomeStatement,
      cashFlowStatement,
      earningsData,
    };
  }

  private findStatementData(statements: any[], periodKey: string): any {
    if (Array.isArray(statements)) {
      return statements.find((stmt: any) => stmt[periodKey])?.[periodKey] || {};
    } else if (statements[periodKey]) {
      return statements[periodKey];
    }
    return {};
  }

  private processBalanceSheet(data: any): BalanceSheetData {
    return {
      totalAssets: data.total_assets || 0,
      currentAssets: data.current_assets || 0,
      totalNonCurrentAssets: data.total_non_current_assets || 0,
      cashAndCashEquivalents: data.cash_and_cash_equivalents || 0,
      accountsReceivable: data.accounts_receivable || 0,
      otherShortTermInvestments: data.other_short_term_investments || 0,
      netPropertyPlantEquipment: data.net_p_p_e || data.property_plant_equipment_net || 0,
      goodwill: data.goodwill || 0,
      totalLiabilities: data.total_liabilities_net_minority_interest || data.total_liabilities || 0,
      currentLiabilities: data.current_liabilities || 0,
      longTermDebt: data.long_term_debt || 0,
      accountsPayable: data.accounts_payable || 0,
      stockholdersEquity: data.stockholders_equity || data.total_equity_gross_minority_interest || 0,
      commonStock: data.common_stock || 0,
      retainedEarnings: data.retained_earnings || 0,
      workingCapital: (data.current_assets || 0) - (data.current_liabilities || 0),
    };
  }

  private processIncomeStatement(statements: any[]): IncomeStatementData {
    const periods = {
      '3M': this.processIncomeStatementPeriod(this.findStatementData(statements, 'period_3m')),
      '6M': this.processIncomeStatementPeriod(this.findStatementData(statements, 'period_6m')),
      '12M': this.processIncomeStatementPeriod(this.findStatementData(statements, 'period_12m')),
    };

    return { periods };
  }

  private processIncomeStatementPeriod(data: any): IncomeStatementPeriod {
    return {
      totalRevenue: data.total_revenue || 0,
      operatingRevenue: data.operating_revenue || data.total_revenue || 0,
      costOfRevenue: data.cost_of_revenue || 0,
      grossProfit: data.gross_profit || 0,
      operatingExpense: data.operating_expense || 0,
      operatingIncome: data.operating_income || 0,
      netIncome: data.net_income || 0,
      ebitda: data.ebitda || 0,
      pretaxIncome: data.pretax_income || 0,
      taxProvision: data.tax_provision || 0,
      researchAndDevelopment: data.research_and_development || 0,
      sellingGeneralAdmin: data.selling_general_and_administration || 0,
    };
  }

  private processCashFlowStatement(statements: any[]): CashFlowStatementData {
    const periods = {
      '3M': this.processCashFlowPeriod(this.findStatementData(statements, 'period_3m')),
      '6M': this.processCashFlowPeriod(this.findStatementData(statements, 'period_6m')),
      '12M': this.processCashFlowPeriod(this.findStatementData(statements, 'period_12m')),
    };

    return { periods };
  }

  private processCashFlowPeriod(data: any): CashFlowPeriod {
    return {
      operatingCashFlow: data.operating_cash_flow || 0,
      investingCashFlow: data.investing_cash_flow || 0,
      financingCashFlow: data.financing_cash_flow || 0,
      freeCashFlow: data.free_cash_flow || 0,
      capitalExpenditure: data.capital_expenditure || 0,
      cashDividendsPaid: data.cash_dividends_paid || 0,
      netIncomeFromContinuingOperations: data.net_income_from_continuing_operations || 0,
      depreciationAndAmortization: data.depreciation_and_amortization || 0,
      changeInWorkingCapital: data.change_in_working_capital || 0,
      beginningCashPosition: data.beginning_cash_position || 0,
      endCashPosition: data.end_cash_position || 0,
    };
  }

  private processEarningsData(statements: any[]): EarningsData {
    const periods = {
      '3M': this.processEarningsPeriod(this.findStatementData(statements, 'period_3m')),
      '6M': this.processEarningsPeriod(this.findStatementData(statements, 'period_6m')),
      '12M': this.processEarningsPeriod(this.findStatementData(statements, 'period_12m')),
    };

    return { periods };
  }

  private processEarningsPeriod(data: any): EarningsPeriod {
    const basicEPS = data.basic_e_p_s || 0;
    const dilutedEPS = data.diluted_e_p_s || 0;
    const dividendPerShare = data.cash_dividends_paid_per_common_share || 0;
    
    return {
      basicEPS,
      dilutedEPS,
      basicAverageShares: data.basic_average_shares || 0,
      dilutedAverageShares: data.diluted_average_shares || 0,
      dividendPerShare,
      dividendCoverageRatio: dividendPerShare > 0 ? basicEPS / dividendPerShare : 0,
    };
  }

  async getFinancialStatements(symbol: string): Promise<FinancialStatements> {
    try {
      logger.debug('Fetching financial data from Tradier', { symbol });
      
      // Fetch real financial data from Tradier beta fundamentals API
      const response = await this.get<any>(`/markets/fundamentals/financials?symbols=${symbol}`);
      
      if (!response || !Array.isArray(response) || response.length === 0) {
        logger.warn('No response or empty array from Tradier API');
        throw new Error('Financial data not available');
      }

      const financialData = response.find((item: any) => 
        item.request === symbol && item.results && item.results.length > 0
      );

      if (!financialData) {
        logger.warn('No financial data found for symbol', { symbol });
        throw new Error('Financial data not available');
      }

      logger.debug('Financial data found, processing');

      // Process the financial data
      const [annualFinancials, quarterlyFinancials] = await Promise.all([
        this.processTradierFinancialData(financialData, 'annual'),
        this.processTradierFinancialData(financialData, 'quarterly')
      ]);

      logger.debug('Processed financial statements', { 
        symbol, 
        annualCount: annualFinancials.length, 
        quarterlyCount: quarterlyFinancials.length 
      });

      return {
        symbol: symbol.toUpperCase(),
        annual: annualFinancials,
        quarterly: quarterlyFinancials,
      };
    } catch (error) {
      logger.error('Error fetching financial statements from Tradier', error);
      throw new Error('Financial data not available');
    }
  }

  private async processTradierFinancialData(financialData: any, freq: 'annual' | 'quarterly'): Promise<FinancialStatement[]> {
    try {
      logger.debug(`Processing ${freq} financial data`);
      const currentYear = new Date().getFullYear();
      const statements: FinancialStatement[] = [];

      // Find the company with financial statements
      const companyWithFinancials = financialData.results.find((result: any) => 
        result.type === 'Company' && result.tables?.financial_statements_restate
      );

      if (!companyWithFinancials?.tables?.financial_statements_restate) {
        logger.warn(`No financial statements found for ${freq} data`);
        return [];
      }

      logger.debug(`Found financial statements for ${freq} processing`);

      const financialStatements = companyWithFinancials.tables.financial_statements_restate;
      const incomeStatements = financialStatements.income_statement || [];
      const balanceSheets = financialStatements.balance_sheet || [];
      const cashFlowStatements = financialStatements.cash_flow_statement || [];

      // Process different periods based on frequency
      const periodsToProcess = freq === 'annual' 
        ? ['period_12m'] 
        : ['period_3m', 'period_6m'];

      periodsToProcess.forEach(periodKey => {
        logger.debug(`Processing period: ${periodKey}`);
        
        // Find matching data across all statements
        // Handle both array and object structures
        let incomeData: any = null;
        let balanceData: any = null;
        let cashFlowData: any = null;

        // Find income statement data
        if (Array.isArray(incomeStatements)) {
          incomeData = incomeStatements.find((stmt: any) => stmt[periodKey]);
        } else if (incomeStatements[periodKey]) {
          incomeData = { [periodKey]: incomeStatements[periodKey] };
        }

        // Find balance sheet data
        if (Array.isArray(balanceSheets)) {
          balanceData = balanceSheets.find((stmt: any) => stmt[periodKey]);
        } else if (balanceSheets[periodKey]) {
          balanceData = { [periodKey]: balanceSheets[periodKey] };
        }

        // Find cash flow data
        if (Array.isArray(cashFlowStatements)) {
          cashFlowData = cashFlowStatements.find((stmt: any) => stmt[periodKey]);
        } else if (cashFlowStatements[periodKey]) {
          cashFlowData = { [periodKey]: cashFlowStatements[periodKey] };
        }

        if (incomeData?.[periodKey] || balanceData?.[periodKey] || cashFlowData?.[periodKey]) {
          const income = incomeData?.[periodKey] || {};
          const balance = balanceData?.[periodKey] || {};
          const cashFlow = cashFlowData?.[periodKey] || {};

          // Extract period information
          const periodEndDate = income.period_ending_date || balance.period_ending_date || cashFlow.period_ending_date;
          let year = currentYear - 1; // Default to last year
          
          if (periodEndDate && periodEndDate !== '1970-01-01') {
            year = new Date(periodEndDate).getFullYear();
          }
          
          // Skip future years (but allow current year data)
          if (year > currentYear) {
            logger.debug(`Skipping future year data: ${year}`);
            return;
          }
          
          logger.debug(`Processing data for year: ${year}`);

          let period: string;
          let quarter: number | undefined;

          if (freq === 'quarterly') {
            if (periodKey === 'period_3m') {
              quarter = 2; // Q2 (most recent quarter)
              period = `Q2 ${year}`;
            } else if (periodKey === 'period_6m') {
              period = `H1 ${year}`;
            } else {
              period = `${year}`;
            }
          } else {
            period = `${year}`;
          }

          // Extract financial values (Tradier returns actual dollar amounts)
          const revenue = income.total_revenue || income.operating_revenue || 0;
          const netIncome = income.net_income || income.net_income_common_stockholders || 0;
          const totalAssets = balance.total_assets || 0;
          const totalDebt = balance.total_debt || balance.long_term_debt_and_capital_lease_obligation || 0;
          const totalEquity = balance.total_equity || balance.stockholders_equity || 0;
          const operatingCashFlow = cashFlow.operating_cash_flow || cashFlow.cash_flow_from_continuing_operating_activities || 0;
          const freeCashFlow = cashFlow.free_cash_flow || 0;
          const grossProfit = income.gross_profit || 0;
          const operatingIncome = income.operating_income || 0;
          const eps = income.diluted_e_p_s || income.basic_e_p_s || 0;

          statements.push({
            period,
            year,
            quarter,
            revenue,
            netIncome,
            eps,
            totalAssets,
            totalDebt,
            totalEquity,
            operatingCashFlow,
            freeCashFlow,
            grossMargin: revenue > 0 ? grossProfit / revenue : 0,
            operatingMargin: revenue > 0 ? operatingIncome / revenue : 0,
            netMargin: revenue > 0 ? netIncome / revenue : 0,
            roe: totalEquity > 0 ? netIncome / totalEquity : 0,
            roa: totalAssets > 0 ? netIncome / totalAssets : 0,
            debtToEquity: totalEquity > 0 ? totalDebt / totalEquity : 0,
          });
        }
      });

      // Sort by year (newest first) and limit to 5 most recent
      const finalStatements = statements
        .sort((a, b) => b.year - a.year)
        .slice(0, 5)
        .reverse(); // Show oldest to newest
      
      logger.debug(`Final ${freq} statements processed`, { count: finalStatements.length });
      
      return finalStatements;
    } catch (error) {
      logger.error(`Error processing ${freq} financial data`, error);
      return [];
    }
  }

  private convertFinancialValue(value: number | string | null | undefined): number {
    if (!value || value === 0 || value === null || value === undefined) return 0;
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 0;
    
    // Tradier returns actual dollar amounts, no conversion needed
    return numValue;
  }

  private getMockCompanyMetrics(symbol: string): CompanyMetrics {
    // Generate realistic mock data based on symbol
    const baseValues = {
      AAPL: { marketCap: 3000000000000, peRatio: 28.5, eps: 6.15, beta: 1.2 },
      MSFT: { marketCap: 2800000000000, peRatio: 32.1, eps: 9.65, beta: 0.9 },
      GOOGL: { marketCap: 1700000000000, peRatio: 25.8, eps: 5.80, beta: 1.1 },
      TSLA: { marketCap: 800000000000, peRatio: 65.2, eps: 4.90, beta: 2.0 },
      AMZN: { marketCap: 1500000000000, peRatio: 45.3, eps: 2.90, beta: 1.3 },
    };

    const mockData = baseValues[symbol as keyof typeof baseValues] || {
      marketCap: 50000000000,
      peRatio: 22.5,
      eps: 3.25,
      beta: 1.1
    };

    return {
      symbol: symbol.toUpperCase(),
      name: `${symbol.toUpperCase()} Inc.`,
      marketCap: mockData.marketCap,
      peRatio: mockData.peRatio,
      eps: mockData.eps,
      revenue: mockData.eps * 4, // Rough estimate
      debtToEquity: 0.35,
      dividendYield: 0.015,
      beta: mockData.beta,
    };
  }

  private getMockRecommendations(symbol: string): AnalystRecommendations {
    // Generate realistic mock recommendations
    const patterns = {
      AAPL: { strongBuy: 8, buy: 12, hold: 5, sell: 2, strongSell: 0 },
      MSFT: { strongBuy: 10, buy: 15, hold: 3, sell: 1, strongSell: 0 },
      GOOGL: { strongBuy: 7, buy: 11, hold: 6, sell: 2, strongSell: 0 },
      TSLA: { strongBuy: 5, buy: 8, hold: 10, sell: 4, strongSell: 1 },
      AMZN: { strongBuy: 9, buy: 13, hold: 4, sell: 2, strongSell: 0 },
    };

    const mockData = patterns[symbol as keyof typeof patterns] || {
      strongBuy: 6,
      buy: 10,
      hold: 7,
      sell: 3,
      strongSell: 1
    };

    return {
      symbol: symbol.toUpperCase(),
      ...mockData,
    };
  }

  private getMockSECFilings(symbol: string, limit: number): SECFiling[] {
    const filings: SECFiling[] = [
      {
        symbol: symbol.toUpperCase(),
        form: '10-K',
        filedDate: '2024-01-30',
        url: '#',
        description: 'Annual Report',
      },
      {
        symbol: symbol.toUpperCase(),
        form: '10-Q',
        filedDate: '2024-10-30',
        url: '#',
        description: 'Quarterly Report',
      },
      {
        symbol: symbol.toUpperCase(),
        form: '8-K',
        filedDate: '2024-11-15',
        url: '#',
        description: 'Current Report',
      },
      {
        symbol: symbol.toUpperCase(),
        form: '10-Q',
        filedDate: '2024-07-30',
        url: '#',
        description: 'Quarterly Report',
      },
      {
        symbol: symbol.toUpperCase(),
        form: 'DEF 14A',
        filedDate: '2024-03-15',
        url: '#',
        description: 'Proxy Statement',
      },
    ];

    return filings.slice(0, limit);
  }
}